import { useEffect } from 'react';
import { Dialog } from '@mui/material';
import { IconX } from '@tabler/icons-react';
import PropTypes from 'prop-types';
import { useForm, yupResolver } from '@mantine/form';
import * as yup from 'yup';

import { Button, DatePicker, IconButton, Input, SelectInput, Textarea, TimePicker } from 'ui';
import { CALENDAR_ITEM_TYPES } from 'common/constants';

const CAMPAIGN_TYPE_OPTIONS = CALENDAR_ITEM_TYPES.map((type) => ({ value: type, label: type }));
const MINIMUM_SEND_TIME_OFFSET_MS = 1000 * 60 * 15; // 15 minutes

const validationSchema = yup.object({
  sendTime: yup
    .date()
    .typeError('Please select a valid send time.')
    .min(new Date(Date.now() + MINIMUM_SEND_TIME_OFFSET_MS), 'Send time must be at least 15 minutes in the future.')
    .required('Send time is required.'),
  topic: yup.string().trim().required('Topic is required.'),
  focus: yup.string().trim().required('Focus is required.'),
  type: yup.string().required('Campaign type is required.'),
});

const getDefaultSendTime = () => {
  const now = new Date(Date.now() + MINIMUM_SEND_TIME_OFFSET_MS);
  const roundedMinutes = Math.ceil(now.getMinutes() / 30) * 30;
  const defaultDate = new Date(now);
  defaultDate.setMinutes(roundedMinutes, 0, 0);
  return defaultDate;
};

const getDefaultValues = () => ({
  sendTime: getDefaultSendTime(),
  topic: '',
  focus: '',
  type: CAMPAIGN_TYPE_OPTIONS[0].value,
});

const SuggestEmailModal = ({ open, onClose, onSubmit, isSubmitting }) => {
  const form = useForm({
    initialValues: getDefaultValues(),
    validate: yupResolver(validationSchema),
    validateInputOnChange: true,
    validateInputOnBlur: true,
  });

  useEffect(() => {
    if (open) {
      const defaults = getDefaultValues();
      form.setValues(defaults);
      form.resetDirty(defaults);
      form.setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    const defaults = getDefaultValues();
    form.setValues(defaults);
    form.resetDirty(defaults);
    form.setErrors({});
    onClose();
  };

  const handleSubmit = () => {
    const validation = form.validate();
    if (validation.hasErrors) return;

    const trimmedValues = {
      ...form.values,
      topic: form.values.topic?.trim() || '',
      focus: form.values.focus?.trim() || '',
    };

    onSubmit(trimmedValues);
  };

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={handleClose}
      sx={{ '.MuiPaper-root': { padding: 0, margin: 0, borderRadius: '12px', width: '100%' } }}
      closeAfterTransition={false}
    >
      <div className="space-y-6 p-6">
        <div className="relative flex items-start">
          <div className="space-y-1 pr-10">
            <h3 className="text-h6 font-semibold">Suggest Additional Email</h3>
            <p className="text-base text-gray-600">Propose a new email to be added to the campaign calendar</p>
          </div>

          <IconButton
            Icon={<IconX size={20} className="text-gray-400" />}
            srOnly="Close"
            onClick={handleClose}
            color="transparent"
            className="absolute -right-1 -top-1 !p-1"
          />
        </div>

        <div className="grid gap-x-4 gap-y-6 md:grid-cols-2">
          <DatePicker
            value={form.values.sendTime}
            onChange={(date) => form.setFieldValue('sendTime', date)}
            required
            disabled={isSubmitting}
            label="Send Date"
            containerClassName="w-full"
          />

          <TimePicker
            value={form.values.sendTime}
            onChange={(date) => form.setFieldValue('sendTime', date)}
            required
            disabled={isSubmitting}
            label="Send Time"
            containerClassName="w-full"
          />

          <SelectInput
            label="Campaign Type"
            value={form.values.type}
            onChange={(e) => form.setFieldValue('type', e.target.value)}
            options={CAMPAIGN_TYPE_OPTIONS}
            size="lg"
            containerClassName="w-full"
            required
            disabled={isSubmitting}
            helperText={form.errors.type}
          />

          <Input
            label="Email Topic"
            value={form.values.topic}
            onChange={(e) => form.setFieldValue('topic', e.target.value)}
            size="xl"
            containerClassName="w-full col-span-full"
            required
            disabled={isSubmitting}
            helperText={form.errors.topic || 'Brief description of the email content'}
            error={!!form.errors.topic}
          />

          <Textarea
            label="Email Focus"
            value={form.values.focus}
            onChange={(e) => form.setFieldValue('focus', e.target.value)}
            size="lg"
            containerClassName="w-full col-span-full"
            required
            disabled={isSubmitting}
            helperText={
              form.errors.focus || 'Detailed description of the email and why it should be added to the calendar'
            }
            error={!!form.errors.focus}
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button title="Cancel" onClick={handleClose} disabled={isSubmitting} />
          <Button
            onClick={handleSubmit}
            color="primary"
            title="Submit Suggestion"
            loading={isSubmitting}
            disabled={isSubmitting}
          />
        </div>
      </div>
    </Dialog>
  );
};

SuggestEmailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};

export default SuggestEmailModal;
